import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import HelpPopover from '../../../HelpPopover';
import { updateParameter } from '../DeviceCardBuilder';
import { DeviceCardProps } from '../DeviceCardProps';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import UnsupportedCodeStatus from './UnsupportedCodeStatus';
import { isUnsupportedEnumRawValue, parseKnownEnumRawValue } from './unsupportedEnumValue';

interface ReferenceFrameControlProps extends DeviceCardProps {
  labelId: string;
  label: React.ReactNode;
  labelPopover?: React.ReactNode;
  labelPopoverId?: string;
  selectId: string;
}

// These enum values must match the enum name.values in the python framework
enum ReferenceFrame {
  FIELD_CENTRIC = 'ReferenceFrame.FIELD_CENTRIC',
  ROBOT_CENTRIC = 'ReferenceFrame.ROBOT_CENTRIC',
}

const REFERENCE_FRAME_VALUES = [ReferenceFrame.FIELD_CENTRIC, ReferenceFrame.ROBOT_CENTRIC] as const;
const UNSUPPORTED_OPTION_VALUE = '__unsupported_custom_code__';

enum DriveType {
  SWERVE = 'DriveType.SWERVE',
  SKID_STEER = 'DriveType.SKID_STEER',
}

const DRIVE_TYPE_VALUES = [DriveType.SWERVE, DriveType.SKID_STEER] as const;

const ReferenceFrameControl = (props: ReferenceFrameControlProps) => {
  const {
    value: referenceFrame,
    setValue: setReferenceFrame,
    unsupportedRawValue,
  } = useControlInitialization<ReferenceFrame>(props, {
    parseValue: (v) => parseKnownEnumRawValue(v, REFERENCE_FRAME_VALUES, ReferenceFrame.FIELD_CENTRIC),
    isUnsupportedRawValue: (rawValue) => isUnsupportedEnumRawValue(rawValue, REFERENCE_FRAME_VALUES),
  });

  const { value: driveType } = useControlInitialization<DriveType>(
    { ...props, parameterName: 'drive_type' },
    {
      parseValue: (v) => parseKnownEnumRawValue(v, DRIVE_TYPE_VALUES, DriveType.SWERVE),
      isUnsupportedRawValue: (rawValue) => isUnsupportedEnumRawValue(rawValue, DRIVE_TYPE_VALUES),
    }
  );

  const defaultFromDriveType = driveType === DriveType.SKID_STEER ? ReferenceFrame.ROBOT_CENTRIC : ReferenceFrame.FIELD_CENTRIC;
  const safeReferenceFrame = referenceFrame ?? defaultFromDriveType;

  const isSkidSteer = driveType === DriveType.SKID_STEER;
  const availableOptions = isSkidSteer ? [ReferenceFrame.ROBOT_CENTRIC] : [ReferenceFrame.FIELD_CENTRIC, ReferenceFrame.ROBOT_CENTRIC];
  const isUnsupported = Boolean(unsupportedRawValue);
  const selectedValue = isUnsupported ? UNSUPPORTED_OPTION_VALUE : isSkidSteer ? ReferenceFrame.ROBOT_CENTRIC : safeReferenceFrame;

  const onChange = (value: string) => {
    if (!REFERENCE_FRAME_VALUES.includes(value as ReferenceFrame)) return;
    if (isSkidSteer) return;
    setReferenceFrame(value as ReferenceFrame);
    updateParameter(props, value);
  };

  const getLabel = (option: ReferenceFrame) => {
    if (option === ReferenceFrame.FIELD_CENTRIC) {
      return <Trans i18nKey="referenceFrameControl.field-centric">Field Centric</Trans>;
    }
    return <Trans i18nKey="referenceFrameControl.robot-centric">Robot Centric</Trans>;
  };

  return (
    <FormControl variant="standard">
      <InputLabel id={props.labelId}>
        {props.labelPopover && props.labelPopoverId ? (
          <Stack direction="row" spacing={1}>
            <Typography>{props.label}</Typography>
            <HelpPopover id={props.labelPopoverId} text={props.labelPopover} />
          </Stack>
        ) : (
          props.label
        )}
      </InputLabel>
      <Select
        labelId={props.labelId}
        id={props.selectId}
        value={selectedValue}
        disabled={isSkidSteer}
        onChange={(event: SelectChangeEvent) => onChange(event.target.value)}
      >
        {isUnsupported && (
          <MenuItem value={UNSUPPORTED_OPTION_VALUE} disabled>
            <Trans i18nKey="controlPanel.devices.unsupportedCode.title">Custom code</Trans>
          </MenuItem>
        )}
        {availableOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {getLabel(option)}
          </MenuItem>
        ))}
      </Select>
      <UnsupportedCodeStatus
        code={unsupportedRawValue}
        descriptionI18nKey="controlPanel.devices.unsupportedEnumParameter.description"
        descriptionDefaultValue="Unsupported setting value: {{code}}"
      />
    </FormControl>
  );
};

export default ReferenceFrameControl;
